/**
 * StaffEarnings — IT-SCE Intern: Verdienst-Konten, Auszahlungen, Registrierungs-Tokens,
 * Aria Multi-Tenant-Registry.
 *
 * TQ #9701 (Staff-Migration Teil G, letzter Sub-Task aus #9523). Portiert aus
 * customer-portal StaffEarnings.tsx + StaffTokens.tsx + StaffAriaTenants.tsx in
 * eine Seite mit 4 Tabs (der Sidebar-Link "Verdienst-Konten" hat nur einen Slot),
 * Backend unveraendert:
 *   - Salden/Auszahlungen: portal-api staff_earnings.go (/api/staff/earnings, /api/staff/payout/*)
 *   - Tokens: portal-api admin-tokens (/api/admin/tokens) — gleicher Host (api.it-sce.de) wie
 *     der uebrige org-portal-Client, daher ueber denselben ApiService, kein separater Client.
 *   - Aria-Tenants: portal-api staff_aria_tenants.go (/api/staff/aria/tenants)
 *
 * Routing: outer-router-level (App.tsx) vor /:slug/*, hinter StaffOnly-Guard.
 * Eigener schlanker Shell (kein CockpitApp-Layout), analog StaffLicenses.tsx.
 */

import { useEffect, useState } from 'react'
import { api } from '../services/api'
import type {
  StaffEmployeeSaldo,
  StaffPayoutProposal,
  StaffAriaTenant,
} from '../services/api'
import type { TToken, TTokenCreateRequest } from '@it-sce/ui'
import { tokenStatus } from '@it-sce/ui'

const fmt = (n: number) =>
  new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(n)

function fmtDate(iso?: string | null): string {
  if (!iso) return '—'
  return new Date(iso).toLocaleDateString('de-DE')
}

// ── Salden-Tab ────────────────────────────────────────────────────────────

function SaldenTab() {
  const [rows, setRows] = useState<StaffEmployeeSaldo[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const load = () => {
    setLoading(true)
    setError(null)
    api
      .getStaffEarnings()
      .then(setRows)
      .catch((err: unknown) => {
        console.error('[StaffEarnings/Salden] load failed:', err)
        setError('Salden konnten nicht geladen werden.')
      })
      .finally(() => setLoading(false))
  }

  useEffect(() => {
    load()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  if (loading) return <p style={{ color: '#666' }}>Lade Salden …</p>
  if (error) return <ErrorBox message={error} onRetry={load} />

  const totalBalance = rows.reduce((sum, r) => sum + r.balance, 0)
  const totalIn = rows.reduce((sum, r) => sum + r.this_month_in, 0)
  const totalOut = rows.reduce((sum, r) => sum + r.this_month_out, 0)

  return (
    <div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 12, marginBottom: 16 }}>
        <StatCard label="Mitarbeiter aktiv" value={String(rows.length)} />
        <StatCard label="Summe Salden" value={fmt(totalBalance)} />
        <StatCard label="Eingang Monat" value={fmt(totalIn)} color="#10b981" />
        <StatCard label="Ausgang Monat" value={fmt(totalOut)} color="#f59e0b" />
      </div>

      <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 8, overflow: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <thead style={{ background: '#f9fafb' }}>
            <tr style={{ textAlign: 'left' }}>
              <th style={{ padding: '10px 12px' }}>Mitarbeiter</th>
              <th style={{ padding: '10px 12px', textAlign: 'right' }}>Saldo</th>
              <th style={{ padding: '10px 12px', textAlign: 'right' }}>Eingang Monat</th>
              <th style={{ padding: '10px 12px', textAlign: 'right' }}>Ausgang Monat</th>
              <th style={{ padding: '10px 12px', textAlign: 'right' }}>Vereinbart</th>
              <th style={{ padding: '10px 12px' }}>Letzte Buchung</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.employee_id} style={{ borderTop: '1px solid #f3f4f6' }}>
                <td style={{ padding: '10px 12px' }}>
                  <strong>{r.employee_name}</strong>
                  <div style={{ fontSize: 11, color: '#9ca3af' }}>{r.employee_id}</div>
                </td>
                <td style={{ padding: '10px 12px', textAlign: 'right', fontWeight: 700, color: r.balance >= 0 ? '#10b981' : '#ef4444' }}>
                  {fmt(r.balance)}
                </td>
                <td style={{ padding: '10px 12px', textAlign: 'right', color: '#10b981' }}>{fmt(r.this_month_in)}</td>
                <td style={{ padding: '10px 12px', textAlign: 'right', color: '#f59e0b' }}>{fmt(r.this_month_out)}</td>
                <td style={{ padding: '10px 12px', textAlign: 'right', color: '#6b7280' }}>{fmt(r.vereinbarte_auszahlung)}</td>
                <td style={{ padding: '10px 12px', color: '#6b7280' }}>{fmtDate(r.last_entry_date)}</td>
              </tr>
            ))}
            {rows.length === 0 && (
              <tr>
                <td colSpan={6} style={{ padding: 32, textAlign: 'center', color: '#9ca3af' }}>
                  Keine Mitarbeiter-Salden gefunden.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  )
}

// ── Auszahlungs-Tab ─────────────────────────────────────────────────────────

interface PayoutItem extends StaffPayoutProposal {
  amount: number
  selected: boolean
  note: string
}

function AuszahlungenTab() {
  const [proposals, setProposals] = useState<PayoutItem[]>([])
  const [confirmToken, setConfirmToken] = useState('')
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [executing, setExecuting] = useState(false)
  const [results, setResults] = useState<{ success: boolean }[] | null>(null)

  const load = () => {
    setLoading(true)
    setError(null)
    setResults(null)
    api
      .getStaffPayoutProposals()
      .then(({ proposals: p, confirm_token }) => {
        setConfirmToken(confirm_token)
        setProposals(
          p.map((item) => ({
            ...item,
            amount: item.proposed_amount,
            selected: item.proposed_amount > 0,
            note: '',
          })),
        )
      })
      .catch((err: unknown) => {
        console.error('[StaffEarnings/Auszahlungen] load failed:', err)
        setError('Vorschläge konnten nicht geladen werden.')
      })
      .finally(() => setLoading(false))
  }

  useEffect(() => {
    load()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const update = (idx: number, patch: Partial<PayoutItem>) => {
    setProposals((curr) => curr.map((p, i) => (i === idx ? { ...p, ...patch } : p)))
  }

  const execute = async () => {
    const items = proposals
      .filter((p) => p.selected && p.amount > 0)
      .map((p) => ({ employee_id: p.employee_id, amount: p.amount, note: p.note }))

    if (items.length === 0) {
      setError('Keine Auszahlungen ausgewählt')
      return
    }
    if (
      !window.confirm(
        `${items.length} Auszahlung(en) anlegen — Summe ${fmt(items.reduce((s, i) => s + i.amount, 0))}. Sicher?`,
      )
    ) {
      return
    }

    setExecuting(true)
    setError(null)
    try {
      const json = await api.postStaffPayoutExecute(confirmToken, items)
      setResults(json.results ?? [])
      setTimeout(load, 1500)
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Fehler beim Ausführen')
    } finally {
      setExecuting(false)
    }
  }

  if (loading) return <p style={{ color: '#666' }}>Lade Vorschläge …</p>

  return (
    <div>
      {error && <ErrorBox message={error} />}

      {results && (
        <div style={{ padding: '1rem', background: '#f0fdf4', borderRadius: 8, color: '#14532d', marginBottom: 16 }}>
          <strong>{results.filter((r) => r.success).length}</strong> Auszahlungen angelegt
          {results.filter((r) => !r.success).length > 0 && (
            <div style={{ marginTop: 8, color: '#991b1b' }}>{results.filter((r) => !r.success).length} fehlgeschlagen</div>
          )}
        </div>
      )}

      {proposals.length === 0 ? (
        <p style={{ color: '#9ca3af', padding: 16 }}>
          Keine Mitarbeiter mit positivem Saldo. Auszahlungen werden vorgeschlagen sobald Verdienst-Konto-Salden &gt; 0 sind.
        </p>
      ) : (
        <>
          <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 8, overflow: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead style={{ background: '#f9fafb' }}>
                <tr style={{ textAlign: 'left' }}>
                  <th style={{ padding: '10px 12px', width: 28 }}>
                    <input
                      type="checkbox"
                      checked={proposals.every((p) => p.selected)}
                      onChange={(e) => setProposals((curr) => curr.map((p) => ({ ...p, selected: e.target.checked })))}
                    />
                  </th>
                  <th style={{ padding: '10px 12px' }}>Mitarbeiter</th>
                  <th style={{ padding: '10px 12px', textAlign: 'right' }}>Saldo</th>
                  <th style={{ padding: '10px 12px', textAlign: 'right' }}>Vorschlag</th>
                  <th style={{ padding: '10px 12px' }}>Notiz</th>
                  <th style={{ padding: '10px 12px', textAlign: 'right' }}>Saldo nachher</th>
                </tr>
              </thead>
              <tbody>
                {proposals.map((p, idx) => (
                  <tr key={p.employee_id} style={{ borderTop: '1px solid #f3f4f6' }}>
                    <td style={{ padding: '10px 12px' }}>
                      <input type="checkbox" checked={p.selected} onChange={(e) => update(idx, { selected: e.target.checked })} />
                    </td>
                    <td style={{ padding: '10px 12px' }}>
                      <strong>{p.employee_name}</strong>
                      <div style={{ fontSize: 11, color: '#9ca3af' }}>{p.employee_id}</div>
                      {p.reason && <div style={{ fontSize: 11, color: '#f59e0b', marginTop: 2 }}>⚠ {p.reason}</div>}
                    </td>
                    <td style={{ padding: '10px 12px', textAlign: 'right', fontWeight: 600 }}>{fmt(p.balance)}</td>
                    <td style={{ padding: '10px 12px', textAlign: 'right' }}>
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        max={p.balance}
                        value={p.amount}
                        onChange={(e) => update(idx, { amount: parseFloat(e.target.value) || 0 })}
                        disabled={!p.selected}
                        style={{ width: 100, textAlign: 'right', padding: '4px 6px' }}
                      />
                    </td>
                    <td style={{ padding: '10px 12px' }}>
                      <input
                        type="text"
                        placeholder="optional"
                        value={p.note}
                        onChange={(e) => update(idx, { note: e.target.value })}
                        disabled={!p.selected}
                        style={{ width: '100%', padding: '4px 6px' }}
                      />
                    </td>
                    <td style={{ padding: '10px 12px', textAlign: 'right', color: '#6b7280' }}>
                      {fmt(p.balance - (p.selected ? p.amount : 0))}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 16, gap: 8 }}>
            <button onClick={load} disabled={executing} style={btnSecondary}>
              Aktualisieren
            </button>
            <button
              onClick={execute}
              disabled={executing || proposals.filter((p) => p.selected && p.amount > 0).length === 0}
              style={{ ...btnPrimary, minWidth: 180 }}
            >
              {executing ? 'Wird ausgeführt …' : `${proposals.filter((p) => p.selected && p.amount > 0).length} Auszahlung(en) anlegen`}
            </button>
          </div>
        </>
      )}
    </div>
  )
}

// ── Tokens-Tab ────────────────────────────────────────────────────────────

function TokensTab() {
  const [tokens, setTokens] = useState<TToken[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [search, setSearch] = useState('')
  const [createOpen, setCreateOpen] = useState(false)
  const [formDescription, setFormDescription] = useState('')
  const [formMaxUses, setFormMaxUses] = useState(1)
  const [formExpiryDays, setFormExpiryDays] = useState(30)
  const [creating, setCreating] = useState(false)
  const [createdToken, setCreatedToken] = useState<TToken | null>(null)

  const REGISTER_BASE = 'https://register.scemail.de'
  const tokenUrl = (token: string) => `${REGISTER_BASE}?token=${encodeURIComponent(token)}`

  const load = () => {
    setLoading(true)
    setError(null)
    api
      .getStaffTokens()
      .then(setTokens)
      .catch((err: unknown) => {
        console.error('[StaffEarnings/Tokens] load failed:', err)
        setError('Tokens konnten nicht geladen werden.')
      })
      .finally(() => setLoading(false))
  }

  useEffect(() => {
    load()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const filtered = tokens.filter((t) => {
    if (!search) return true
    const q = search.toLowerCase()
    return (t.token + ' ' + (t.description ?? '') + ' ' + (t.created_by ?? '')).toLowerCase().includes(q)
  })

  const copyToClipboard = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text)
    } catch {
      /* Clipboard-API kann in unsicheren Kontexten fehlen — kein Blocker */
    }
  }

  const handleCreate = async () => {
    setCreating(true)
    setError(null)
    try {
      const req: TTokenCreateRequest = {
        description: formDescription.trim() || undefined,
        max_uses: formMaxUses,
        expires_at: formExpiryDays > 0 ? new Date(Date.now() + formExpiryDays * 86_400_000).toISOString() : undefined,
      }
      const token = await api.postStaffToken(req)
      setCreatedToken(token)
      setCreateOpen(false)
      setFormDescription('')
      setFormMaxUses(1)
      setFormExpiryDays(30)
      load()
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Token konnte nicht erstellt werden')
    } finally {
      setCreating(false)
    }
  }

  const handleRevoke = async (t: TToken) => {
    if (!window.confirm(`Token "${t.token}"${t.description ? ` (${t.description})` : ''} widerrufen?`)) return
    try {
      await api.deleteStaffToken(t.id)
      load()
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Widerruf fehlgeschlagen')
    }
  }

  if (loading) return <p style={{ color: '#666' }}>Lade Tokens …</p>
  if (error) return <ErrorBox message={error} onRetry={load} />

  return (
    <div>
      <div style={{ display: 'flex', gap: 8, marginBottom: 16, alignItems: 'center', flexWrap: 'wrap' }}>
        <input
          type="search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Suche Token, Notiz, Ersteller…"
          style={{ flex: 1, minWidth: 200, padding: '8px 12px', border: '1px solid #e5e7eb', borderRadius: 6 }}
        />
        <button onClick={() => setCreateOpen((v) => !v)} style={btnPrimary}>
          {createOpen ? 'Abbrechen' : '+ Neuer Token'}
        </button>
      </div>

      {createOpen && (
        <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 8, padding: 16, marginBottom: 16 }}>
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'flex-end' }}>
            <label style={{ flex: 2, minWidth: 200 }}>
              <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 4 }}>Notiz (optional)</div>
              <input
                type="text"
                value={formDescription}
                onChange={(e) => setFormDescription(e.target.value)}
                maxLength={500}
                placeholder="z.B. Max Mustermann – Neukunde"
                style={{ width: '100%', padding: '6px 10px', border: '1px solid #e5e7eb', borderRadius: 6 }}
              />
            </label>
            <label>
              <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 4 }}>Max. Nutzungen</div>
              <input
                type="number"
                min={1}
                max={1000}
                value={formMaxUses}
                onChange={(e) => setFormMaxUses(parseInt(e.target.value || '1', 10))}
                style={{ width: 90, padding: '6px 10px', border: '1px solid #e5e7eb', borderRadius: 6 }}
              />
            </label>
            <label>
              <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 4 }}>Gültigkeit (Tage)</div>
              <select
                value={formExpiryDays}
                onChange={(e) => setFormExpiryDays(parseInt(e.target.value, 10))}
                style={{ padding: '6px 10px', border: '1px solid #e5e7eb', borderRadius: 6 }}
              >
                {[1, 7, 14, 30, 60, 90, 180, 365].map((d) => (
                  <option key={d} value={d}>
                    {d} Tage
                  </option>
                ))}
              </select>
            </label>
            <button onClick={handleCreate} disabled={creating} style={btnPrimary}>
              {creating ? 'Erstellt …' : 'Erstellen'}
            </button>
          </div>
        </div>
      )}

      {createdToken && (
        <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 8, padding: '10px 14px', marginBottom: 16, display: 'flex', alignItems: 'center', gap: 10 }}>
          <span>Token erstellt:</span>
          <code style={{ fontWeight: 700 }}>{createdToken.token}</code>
          <button onClick={() => copyToClipboard(tokenUrl(createdToken.token))} style={{ ...btnSecondary, padding: '4px 10px', fontSize: 12 }}>
            URL kopieren
          </button>
          <button onClick={() => setCreatedToken(null)} style={{ marginLeft: 'auto', border: 'none', background: 'none', color: '#6b7280', cursor: 'pointer' }}>
            ✕
          </button>
        </div>
      )}

      <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 8, overflow: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <thead style={{ background: '#f9fafb' }}>
            <tr style={{ textAlign: 'left' }}>
              <th style={{ padding: '10px 12px' }}>Status</th>
              <th style={{ padding: '10px 12px' }}>Token</th>
              <th style={{ padding: '10px 12px' }}>Notiz</th>
              <th style={{ padding: '10px 12px', textAlign: 'center' }}>Nutzungen</th>
              <th style={{ padding: '10px 12px' }}>Gültig bis</th>
              <th style={{ padding: '10px 12px' }}>Erstellt</th>
              <th style={{ padding: '10px 12px' }} />
            </tr>
          </thead>
          <tbody>
            {filtered.map((t) => {
              const s = tokenStatus(t)
              const label = s === 'active' ? 'Aktiv' : s === 'consumed' ? 'Verbraucht' : 'Abgelaufen'
              const bg = s === 'active' ? '#d1fae5' : s === 'consumed' ? '#e5e7eb' : '#fee2e2'
              const fg = s === 'active' ? '#065f46' : s === 'consumed' ? '#374151' : '#991b1b'
              const exhausted = t.max_uses != null && t.current_uses >= t.max_uses
              return (
                <tr key={t.id} style={{ borderTop: '1px solid #f3f4f6' }}>
                  <td style={{ padding: '10px 12px' }}>
                    <span style={{ padding: '2px 8px', borderRadius: 12, fontSize: 11, fontWeight: 600, background: bg, color: fg }}>{label}</span>
                  </td>
                  <td style={{ padding: '10px 12px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <code style={{ fontWeight: 600 }}>{t.token}</code>
                      <button onClick={() => copyToClipboard(tokenUrl(t.token))} title="Registrierungs-URL kopieren" style={{ border: 'none', background: 'none', cursor: 'pointer' }}>
                        📋
                      </button>
                    </div>
                  </td>
                  <td style={{ padding: '10px 12px' }}>{t.description || <span style={{ color: '#9ca3af' }}>—</span>}</td>
                  <td style={{ padding: '10px 12px', textAlign: 'center', fontWeight: exhausted ? 700 : 400, color: exhausted ? '#dc2626' : 'inherit' }}>
                    {t.current_uses}
                    {t.max_uses != null ? ` / ${t.max_uses}` : ''}
                  </td>
                  <td style={{ padding: '10px 12px' }}>{fmtDate(t.expires_at)}</td>
                  <td style={{ padding: '10px 12px', color: '#6b7280' }}>
                    {fmtDate(t.created_at)}
                    {t.created_by && <div style={{ fontSize: 11 }}>von {t.created_by}</div>}
                  </td>
                  <td style={{ padding: '10px 12px', textAlign: 'right' }}>
                    {s === 'active' && (
                      <button onClick={() => handleRevoke(t)} style={{ border: '1px solid #fecaca', background: '#fff', color: '#dc2626', padding: '4px 10px', borderRadius: 6, cursor: 'pointer', fontSize: 12 }}>
                        Widerrufen
                      </button>
                    )}
                  </td>
                </tr>
              )
            })}
            {filtered.length === 0 && (
              <tr>
                <td colSpan={7} style={{ padding: 32, textAlign: 'center', color: '#9ca3af' }}>
                  {tokens.length === 0 ? 'Noch keine Tokens angelegt' : 'Kein Token passt zu den Filtern'}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  )
}

// ── Aria-Tenants-Tab ────────────────────────────────────────────────────────

function AriaTenantsTab() {
  const [tenants, setTenants] = useState<StaffAriaTenant[]>([])
  const [loading, setLoading] = useState(true)
  const [flash, setFlash] = useState<{ kind: 'ok' | 'err'; msg: string } | null>(null)
  const [showForm, setShowForm] = useState(false)
  const [formTenant, setFormTenant] = useState('')
  const [formRealm, setFormRealm] = useState('')
  const [formSecret, setFormSecret] = useState('')
  const [formNote, setFormNote] = useState('')
  const [submitting, setSubmitting] = useState(false)

  const load = () => {
    setLoading(true)
    api
      .getStaffAriaTenants()
      .then(setTenants)
      .catch((err: unknown) => {
        console.error('[StaffEarnings/AriaTenants] load failed:', err)
        setFlash({ kind: 'err', msg: 'Laden fehlgeschlagen' })
      })
      .finally(() => setLoading(false))
  }

  useEffect(() => {
    load()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setSubmitting(true)
    setFlash(null)
    try {
      await api.postStaffAriaTenant({
        tenant: formTenant.trim().toLowerCase(),
        realm: formRealm.trim(),
        client_secret: formSecret,
        note: formNote.trim(),
      })
      setFlash({ kind: 'ok', msg: `Tenant "${formTenant}" registriert.` })
      setFormTenant('')
      setFormRealm('')
      setFormSecret('')
      setFormNote('')
      setShowForm(false)
      load()
    } catch (err: unknown) {
      setFlash({ kind: 'err', msg: err instanceof Error ? err.message : 'Speichern fehlgeschlagen' })
    } finally {
      setSubmitting(false)
    }
  }

  const handleStatus = async (tenant: string, status: 'active' | 'disabled') => {
    try {
      await api.patchStaffAriaTenantStatus(tenant, status)
      setFlash({ kind: 'ok', msg: `Tenant "${tenant}" → ${status}` })
      load()
    } catch (err: unknown) {
      setFlash({ kind: 'err', msg: err instanceof Error ? err.message : 'Update fehlgeschlagen' })
    }
  }

  const handleDelete = async (tenant: string) => {
    if (!window.confirm(`Tenant "${tenant}" wirklich komplett entfernen? Audit-Eintrag bleibt erhalten, aber Bootstrap geht nicht mehr.`)) return
    try {
      await api.deleteStaffAriaTenant(tenant)
      setFlash({ kind: 'ok', msg: `Tenant "${tenant}" entfernt.` })
      load()
    } catch (err: unknown) {
      setFlash({ kind: 'err', msg: err instanceof Error ? err.message : 'Löschen fehlgeschlagen' })
    }
  }

  return (
    <div>
      <p style={{ color: '#6b7280', fontSize: 13, marginTop: 0, marginBottom: 16 }}>
        Pro Tenant ein Eintrag mit dem aria-service Client-Secret. Wird vom Hub via{' '}
        <code>POST /api/aria/bootstrap</code> abgeholt. Env-var-Tenants (ITSCE / EZA / PERSONAL aus K8s-Secret)
        werden hier nicht angezeigt — die sind hart konfiguriert und gewinnen vor der DB.
      </p>

      {flash && (
        <div
          style={{
            padding: '10px 14px',
            borderRadius: 8,
            marginBottom: 16,
            background: flash.kind === 'ok' ? '#f0fdf4' : '#fef2f2',
            color: flash.kind === 'ok' ? '#14532d' : '#991b1b',
            border: `1px solid ${flash.kind === 'ok' ? '#bbf7d0' : '#fecaca'}`,
          }}
        >
          {flash.msg}
        </div>
      )}

      <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
        <button onClick={() => setShowForm((s) => !s)} style={btnPrimary}>
          {showForm ? 'Abbrechen' : '+ Tenant hinzufügen / Secret rotieren'}
        </button>
        <button onClick={load} style={btnSecondary}>
          Neu laden
        </button>
      </div>

      {showForm && (
        <form onSubmit={handleSubmit} style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 8, padding: 16, marginBottom: 16 }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 12, marginBottom: 12 }}>
            <label>
              <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 4 }}>Tenant-Slug *</div>
              <input
                type="text"
                value={formTenant}
                onChange={(e) => setFormTenant(e.target.value)}
                placeholder="z.B. glasbrenner"
                pattern="[a-z0-9_-]+"
                required
                style={{ width: '100%', padding: '6px 10px', border: '1px solid #e5e7eb', borderRadius: 6 }}
              />
            </label>
            <label>
              <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 4 }}>Keycloak Realm *</div>
              <input
                type="text"
                value={formRealm}
                onChange={(e) => setFormRealm(e.target.value)}
                placeholder="z.B. glasbrenner"
                required
                style={{ width: '100%', padding: '6px 10px', border: '1px solid #e5e7eb', borderRadius: 6 }}
              />
            </label>
            <label>
              <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 4 }}>Client-Secret *</div>
              <input
                type="password"
                value={formSecret}
                onChange={(e) => setFormSecret(e.target.value)}
                placeholder="aus Keycloak Admin UI kopieren"
                required
                autoComplete="off"
                style={{ width: '100%', padding: '6px 10px', border: '1px solid #e5e7eb', borderRadius: 6 }}
              />
            </label>
          </div>
          <label style={{ display: 'block', marginBottom: 12 }}>
            <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 4 }}>Notiz</div>
            <textarea
              value={formNote}
              onChange={(e) => setFormNote(e.target.value)}
              rows={2}
              placeholder="Welche Org / wer hat bestellt / wann ablaufend …"
              style={{ width: '100%', padding: '6px 10px', border: '1px solid #e5e7eb', borderRadius: 6 }}
            />
          </label>
          <button type="submit" disabled={submitting} style={btnPrimary}>
            {submitting ? 'Speichert …' : 'Speichern'}
          </button>
        </form>
      )}

      {loading ? (
        <p style={{ color: '#666' }}>Lade Tenants …</p>
      ) : tenants.length === 0 ? (
        <p style={{ color: '#9ca3af' }}>Keine DB-registrierten Tenants. Env-var-Tenants aus K8s-Secret werden separat verwaltet.</p>
      ) : (
        <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 8, overflow: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead style={{ background: '#f9fafb' }}>
              <tr style={{ textAlign: 'left' }}>
                <th style={{ padding: '10px 12px' }}>Tenant</th>
                <th style={{ padding: '10px 12px' }}>Realm</th>
                <th style={{ padding: '10px 12px' }}>Status</th>
                <th style={{ padding: '10px 12px' }}>Secret</th>
                <th style={{ padding: '10px 12px' }}>Notiz</th>
                <th style={{ padding: '10px 12px' }}>Erstellt</th>
                <th style={{ padding: '10px 12px' }} />
              </tr>
            </thead>
            <tbody>
              {tenants.map((t) => (
                <tr key={t.tenant} style={{ borderTop: '1px solid #f3f4f6', opacity: t.status === 'disabled' ? 0.6 : 1 }}>
                  <td style={{ padding: '10px 12px' }}>
                    <code>{t.tenant}</code>
                  </td>
                  <td style={{ padding: '10px 12px' }}>
                    <code>{t.realm}</code>
                  </td>
                  <td style={{ padding: '10px 12px' }}>
                    <span
                      style={{
                        padding: '2px 8px',
                        borderRadius: 12,
                        fontSize: 11,
                        fontWeight: 600,
                        background: t.status === 'active' ? '#d1fae5' : '#fef3c7',
                        color: t.status === 'active' ? '#065f46' : '#92400e',
                      }}
                    >
                      {t.status}
                    </span>
                  </td>
                  <td style={{ padding: '10px 12px' }}>
                    <code style={{ fontSize: 11 }}>{t.client_secret_redacted}</code>
                  </td>
                  <td style={{ padding: '10px 12px' }}>{t.note || '—'}</td>
                  <td style={{ padding: '10px 12px', color: '#6b7280' }}>
                    {fmtDate(t.created_at)}
                    {t.created_by_user_email && <div style={{ fontSize: 11 }}>von {t.created_by_user_email}</div>}
                  </td>
                  <td style={{ padding: '10px 12px', textAlign: 'right', whiteSpace: 'nowrap' }}>
                    {t.status === 'active' ? (
                      <button onClick={() => handleStatus(t.tenant, 'disabled')} style={{ ...btnSecondary, padding: '4px 10px', fontSize: 12, marginRight: 6 }}>
                        Deaktivieren
                      </button>
                    ) : (
                      <button onClick={() => handleStatus(t.tenant, 'active')} style={{ ...btnSecondary, padding: '4px 10px', fontSize: 12, marginRight: 6 }}>
                        Reaktivieren
                      </button>
                    )}
                    <button onClick={() => handleDelete(t.tenant)} style={{ border: '1px solid #fecaca', background: '#fff', color: '#dc2626', padding: '4px 10px', borderRadius: 6, cursor: 'pointer', fontSize: 12 }}>
                      Löschen
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}

// ── Shared UI helpers ─────────────────────────────────────────────────────

function StatCard({ label, value, color }: { label: string; value: string; color?: string }) {
  return (
    <div style={{ padding: 14, background: '#fff', border: '1px solid #e5e7eb', borderRadius: 8 }}>
      <div style={{ color: '#6b7280', fontSize: 12 }}>{label}</div>
      <div style={{ fontSize: 18, fontWeight: 600, color: color ?? '#111827' }}>{value}</div>
    </div>
  )
}

function ErrorBox({ message, onRetry }: { message: string; onRetry?: () => void }) {
  return (
    <div style={{ padding: '1rem', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 6, color: '#b91c1c', marginBottom: 16 }}>
      {message}{' '}
      {onRetry && (
        <button onClick={onRetry} style={{ marginLeft: 8, textDecoration: 'underline', background: 'none', border: 'none', color: '#b91c1c', cursor: 'pointer' }}>
          Erneut versuchen
        </button>
      )}
    </div>
  )
}

const btnPrimary: React.CSSProperties = {
  padding: '8px 16px',
  background: '#FF6B35',
  color: '#fff',
  border: 'none',
  borderRadius: 6,
  fontWeight: 600,
  cursor: 'pointer',
  fontSize: 13,
}

const btnSecondary: React.CSSProperties = {
  padding: '8px 16px',
  background: '#fff',
  color: '#374151',
  border: '1px solid #e5e7eb',
  borderRadius: 6,
  cursor: 'pointer',
  fontSize: 13,
}

// ── Page shell ────────────────────────────────────────────────────────────

type Tab = 'salden' | 'auszahlungen' | 'tokens' | 'aria'

const TABS: { key: Tab; icon: string; label: string }[] = [
  { key: 'salden', icon: '📊', label: 'Salden' },
  { key: 'auszahlungen', icon: '💸', label: 'Auszahlungen' },
  { key: 'tokens', icon: '🎫', label: 'Registrierungs-Tokens' },
  { key: 'aria', icon: '🤖', label: 'Aria-Tenants' },
]

export default function StaffEarnings() {
  const [tab, setTab] = useState<Tab>('salden')

  return (
    <div style={{ maxWidth: 1200, margin: '0 auto', padding: '2rem 1.5rem' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.5rem' }}>
        <a href="/" style={{ color: '#FF6B35', textDecoration: 'none', fontSize: '0.9rem' }}>
          ← Cockpit
        </a>
      </div>
      <h1 style={{ margin: '0 0 0.5rem', fontSize: '1.6rem' }}>💰 Verdienst-Konten</h1>
      <p style={{ color: '#666', marginTop: 0, marginBottom: '1rem', fontSize: '0.9rem' }}>
        Salden &amp; Auszahlungen aller IT-SCE-Mitarbeiter, Registrierungs-Tokens und die Aria Multi-Tenant-Registry.
      </p>

      <div style={{ display: 'flex', gap: 8, marginBottom: '1.25rem', borderBottom: '1px solid #e5e7eb', flexWrap: 'wrap' }}>
        {TABS.map((t) => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            style={{
              padding: '8px 16px',
              border: 'none',
              borderBottom: tab === t.key ? '2px solid #FF6B35' : '2px solid transparent',
              background: 'none',
              color: tab === t.key ? '#FF6B35' : '#6b7280',
              fontWeight: tab === t.key ? 600 : 400,
              cursor: 'pointer',
              fontSize: 14,
            }}
          >
            {t.icon} {t.label}
          </button>
        ))}
      </div>

      {tab === 'salden' && <SaldenTab />}
      {tab === 'auszahlungen' && <AuszahlungenTab />}
      {tab === 'tokens' && <TokensTab />}
      {tab === 'aria' && <AriaTenantsTab />}
    </div>
  )
}
